import React, { Component, Context } from "react";
import { RouteComponentProps, withRouter, NavLink } from "react-router-dom";
import clsx from "clsx";
import Grid from "../grid";
import { ColDef } from "ag-grid-community";
import { getFormSubmissions, getForm, deleteDocument } from "../../laco-common/base/firestore-utils";
import { isSystemForm, IForm } from "../../laco-common";
import { IFormsGridContext } from "../grid/columns";
import { getFormSubmissionColDefs, getColDefsFromForm } from "../grid/columns/utils";
import { FirebaseContext } from "../../laco-common/firebase/context";
import { IFormSubmission } from "../../laco-common/base/models/formSubmission";
import { withAuth, IAuthProps } from "../../laco-common/auth/AuthContext";
import { WidgetProps } from "../widgets";
import { IUserAuthPayload } from "../../laco-common/base/models/user";
import { FIREWIRE_TENANT } from "../../laco-common/base/models/tenant";
import { ISitePage, ICMSPage } from "../../laco-common/base/models/page";
import { IFirebaseContext, IClientFirebase } from "../../laco-common/base/firebase";
import { withCmsRoleContext, CMSRoleContextProps } from "../../admin/contexts/cms-role/CMSRole";
const queryString = require("query-string");

type FormSubmissionsProps = {
	page: ISitePage;
	className?: string;
	fullWidth?: boolean;
};

type FormSubmissionsState = {
	loading: boolean;
	isAdmin: boolean;
	searchQuery: string;
	columnDefs: ColDef[];
	formSubmissions: IFormSubmission[];
	formsGridContext: IFormsGridContext;
	formId?: string;
	form?: IForm;
};

const collectionName: string = "form_submissions";
class FormSubmissions extends Component<
	RouteComponentProps & IAuthProps & WidgetProps & FormSubmissionsProps & CMSRoleContextProps,
	FormSubmissionsState
> {
	static contextType: Context<IFirebaseContext> = FirebaseContext;
	private readonly useFirewire: boolean = (this.props.page as ICMSPage)?.useFirewire;
	private readonly clientFirebase: IClientFirebase = this.useFirewire
		? this.context.firewireApp
		: this.context.secondClientFirebase || this.context.clientFirebase;

	state = {
		loading: true,
		isAdmin: false,
		searchQuery: "",
		columnDefs: [],
		formSubmissions: null,
		formsGridContext: { formConfigMap: {} },
		formId: undefined,
		form: null,
	};

	async componentDidMount() {
		this.init();
	}

	async componentDidUpdate(prevProps) {
		if (this.props.location.search !== prevProps.location.search) {
			this.init();
		}
	}

	async init() {
		await this.loadForm();
		this.loadFormSubmissions();
	}

	async loadForm() {
		this.setState({ loading: true });
		const formId = queryString.parse(this.props.location.search)?.formId;
		if (!formId) {
			this.setState({
				formId: undefined,
				form: null,
			});
			return;
		}
		const form: IForm = await getForm(formId, this.clientFirebase.getFirebase());
		this.setState({ formId, form });
	}

	async loadFormSubmissions(): Promise<any> {
		const userPayload: IUserAuthPayload = this.props.authContext.userPayload;
		const context: IFirebaseContext = {
			...this.context,
			clientFirebase: this.clientFirebase,
			userPayload,
		};
		const isAdmin: boolean =
			context.userPayload.superAdmin ||
			this.props.cmsRoleContext.hasPermission("form-submissions-management") ||
			(context.tenant === FIREWIRE_TENANT &&
				(context.userPayload.psAdmin || context.userPayload.psRole === "admin")) ||
			(context.tenant !== FIREWIRE_TENANT && context.userPayload.role === "admin"); // TODO: gary handle team role
		this.setState({ isAdmin });

		getFormSubmissions(context, isAdmin, undefined, this.state.formId)
			.then(async (formSubmissions: IFormSubmission[]) => {
				let formIdsMap = {};
				formSubmissions.forEach((fs: IFormSubmission) => {
					if (!isSystemForm(fs.formId)) {
						formIdsMap[fs.formId] = fs.formId;
					}
				});
				const formIds: string[] = Object.keys(formIdsMap);
				let colDefs: ColDef[] = [];
				let formsGridContext: IFormsGridContext;
				if (formIds.length === 1) {
					const form: IForm =
						this.state.form || (await getForm(formIds[0], this.clientFirebase.getFirebase()));
					colDefs = getColDefsFromForm(form);
					formsGridContext = { formConfigMap: { [formIds[0]]: form } };
				} else {
					const response = await getFormSubmissionColDefs(context, formIds, isAdmin);
					colDefs = response.colDefs;
					formsGridContext = response.formsGridContext;
				}
				this.setState({ loading: false, columnDefs: colDefs, formSubmissions, formsGridContext });
			})
			.finally(() => {
				this.setState({ loading: false });
			})
			.catch((err) => {
				console.error(err);
				this.setState({ formSubmissions: [] });
			});
	}

	async onDelete(data: any): Promise<any> {
		console.log("FormSubmissions.onDelete(): data", data);
		await deleteDocument(this.clientFirebase, collectionName, data.id);
		return this.loadFormSubmissions();
	}

	renderCustomTitle() {
		if (!this.state.form) return null;
		return (
			<div className="mb-0 pb-0">
				<h2 className="d-inline" style={{ fontSize: "18px" }}>
					<NavLink to={`/admin/${this.useFirewire ? "ps-" : ""}form-submissions`}>
						{this.props.page?.title || "Form Submissions"}
					</NavLink>
				</h2>
				<span className="px-2">&#62;</span>
				<h2 className="d-inline" style={{ fontSize: "15px" }}>
					{this.state.form.name}
				</h2>
			</div>
		);
	}

	render() {
		return (
			<div
				className={clsx(
					!this.props.fullWidth && "container",
					this.props.fullWidth && "container-fluid px-3",
					!this.props.isNested && !this.props.className && "section-padding",
					this.props.className
				)}
			>
				<Grid
					displayTitle={!this.state.form}
					customTitle={this.renderCustomTitle()}
					columnDefs={this.state.columnDefs}
					rowData={this.state.formSubmissions}
					loading={this.state.loading}
					onRefresh={this.loadFormSubmissions.bind(this)}
					withUserCols={true}
					isAdmin={this.state.isAdmin}
					gridOptions={{
						context: this.state.formsGridContext,
					}}
					exportFilename="Form Submissions"
					noDataMsg="No Form Submissions"
					entityName={this.props.page?.title || "Form Submissions"}
					actionProps={{
						onDelete: this.props.cmsRoleContext.hasPermission("super-admin")
							? this.onDelete.bind(this)
							: null,
					}}
				/>
			</div>
		);
	}
}

export default withRouter(withCmsRoleContext(withAuth(FormSubmissions)));
